import { FC, useCallback, useEffect, useState } from 'react';
import { Field, useFormikContext } from 'formik';
import { IconButton, Theme, Tooltip, Box, Stack } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import SyncIcon from '@mui/icons-material/Sync';
import { useTranslation } from 'mir-utils/i18n';
import { IdecoTextField } from '../IdecoTextField';
import { CopyIcon } from '../CopyElements/CopyIcon';
import BadgePassword from '../BadgePassword';
import { FieldsWrapper } from '../FieldsWrapper';
import { generatePassword } from './utils';

const useStyles = makeStyles((theme: Theme) => ({
  helperText: {
    maxWidth: '308px',
  },
  greenColor: {
    color: theme.palette.success.main,
  },
  fieldSize: {
    maxWidth: 328,
  },
}));

/**
 * @summary Общий компонент для поля ввода пароля.
 *
 * @param eye - Добавить внутрь поля кнопку, позволяющую показать пароль.
 * @param generate - Добавить справа от поля ввода кнопку, генерирующую случайный пароль.
 * @param copy - Добавить справа от поля ввода кнопку, копирующую содержимое поля
 * в буфер обмена.
 * @param autoComplete - Свойство autoComplete для input-а. Если не передаётся параметр
 * 'confirmFieldAutoComplete', то это свойство проставится и во второе поле, если оно есть.
 * @param errorFlag - Флаг того, что произошла ошибка. Нужно для некоторых случаев, таких,
 * как ошибка при авторизации.
 * @param name - Name для input-а.
 * @param label - Заголовок поля.
 * @param id - Id, передаваемый input-у.
 * @param badge - Показывать плашечку(BadgePassword), отображающую степень надёжности пароля.
 * @param hasConfirm - Добавить второе поле для подтверждения пароля.
 * @param autoGenerate - Если передаётся, то в поле изначально будет сгенерирован случайный
 * пароль.
 * @param confirmFieldName - Name для поля подтверждения пароля.
 * @param confirmFieldLabel - Заголовок поля подтверждения пароля.
 * @param confirmFieldHelperText - Подсказка под полем подтверждения.
 * @param confirmFieldAutoComplete - Свойство autoComplete для второго input-а.
 *
 * @see BadgePassword
 */
export const PasswordWithButtons: FC<{
  confirmFieldName?: string;
  confirmFieldLabel?: string;
  confirmFieldHelperText?: string;
  confirmFieldAutoComplete?: string;
  eye?: boolean;
  generate?: boolean;
  copy?: boolean;
  autoComplete?: string;
  errorFlag?: boolean;
  name?: string;
  label?: string;
  id?: string;
  badge?: boolean;
  hasConfirm?: boolean;
  autoGenerate?: boolean;
}> = ({
  eye,
  generate,
  copy,
  autoComplete = 'new-password',
  errorFlag,
  name,
  label,
  id,
  badge = true,
  hasConfirm = false,
  confirmFieldLabel,
  confirmFieldName = 'repeatedPassword',
  confirmFieldHelperText,
  confirmFieldAutoComplete,
  autoGenerate,
}) => {
  const { values, setValues, errors } = useFormikContext<any>();

  const classes = useStyles({});
  const { t } = useTranslation();

  const [passwordVisible, setPasswordVisibility] = useState<boolean>(false);
  const [isAutoGenerated, setIsAutoGenerated] = useState<boolean>(false);

  const onGeneratePassword = useCallback(() => {
    if (autoGenerate) {
      setIsAutoGenerated(true);
    }

    const generatedPassword = generatePassword();
    const valuesStaged = hasConfirm
      ? {
          ...values,
          password: generatedPassword,
          [confirmFieldName]: generatedPassword,
        }
      : {
          ...values,
          password: generatedPassword,
        };

    setValues({ ...valuesStaged });
  }, [autoGenerate, hasConfirm, values, confirmFieldName, setValues]);

  useEffect(() => {
    if (autoGenerate && !isAutoGenerated) {
      onGeneratePassword();
    }
  }, [autoGenerate, isAutoGenerated, onGeneratePassword]);

  return (
    <Stack
      spacing={2}
      sx={{
        width: '100%',
      }}
    >
      <Field
        id={id || 'password'}
        type={passwordVisible ? 'text' : 'password'}
        name={name || 'password'}
        component={IdecoTextField}
        autoComplete={autoComplete}
        variant="outlined"
        label={label || t('Password')}
        InputProps={{
          ...(eye && {
            endAdornment: (
              <Tooltip title={passwordVisible ? t('Hide password') : t('Show password')}>
                <IconButton
                  tabIndex={-1}
                  edge="end"
                  onClick={() => setPasswordVisibility(!passwordVisible)}
                  size="small"
                >
                  {passwordVisible ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </Tooltip>
            ),
          }),
          ...(errorFlag && {
            error: errorFlag,
          }),
        }}
        {...(errorFlag && {
          InputLabelProps: {
            error: errorFlag,
          },
        })}
      />
      {(generate || copy) && (
        // mt={1.5} - из-за того, что высота поля меняется при появлении ошибок валидации,
        // центрировать через alignItems="center" не получится.
        <Box display="flex" alignItems="flex-start" ml={1} mt={1.5}>
          {generate && (
            <Tooltip title={t('Generate password')}>
              <IconButton onClick={onGeneratePassword} size="small">
                <SyncIcon />
              </IconButton>
            </Tooltip>
          )}
          {copy && <CopyIcon size="small" text={values.password} />}
        </Box>
      )}
      {hasConfirm && (
        <Field
          type={passwordVisible ? 'text' : 'password'}
          name={confirmFieldName}
          component={IdecoTextField}
          autoComplete={confirmFieldAutoComplete || autoComplete}
          FormHelperTextProps={{
            className: classes.helperText,
          }}
          variant="outlined"
          label={confirmFieldLabel}
          margin={'dense'}
          helperText={confirmFieldHelperText}
          {...(errorFlag && {
            InputProps: {
              error: errorFlag,
            },
          })}
          {...(errorFlag && {
            InputLabelProps: {
              error: errorFlag,
            },
          })}
        />
      )}
      {values.password && !errors.password && badge && (
        <FieldsWrapper>
          <BadgePassword password={values.password} />
        </FieldsWrapper>
      )}
    </Stack>
  );
};
